// "Sign in with Google" integration for the web PWA, using Google Identity
// Services (GIS). The native app uses expo-auth-session; on the web we load the
// GIS SDK and use the "auth code" popup flow: GIS returns a one-time
// authorization code that the server exchanges (redirect_uri "postmessage") for
// an id_token — mirroring the Apple authorization-code flow.
//
// The popup is opened by GIS from `requestCode()`, so callers should invoke
// `signInWithGoogle()` from a direct user gesture (the button's onPress).

import { GOOGLE_WEB_CLIENT_ID } from "../config/constants";

const GIS_SDK_URL = "https://accounts.google.com/gsi/client";

let sdkPromise = null;
let codeClient = null;
// The GIS code client takes a single callback at creation time, so we relay the
// result to the in-flight signInWithGoogle() promise via this deferred.
let pending = null;

const loadSdk = () => {
	if (typeof window !== "undefined" && window.google?.accounts?.oauth2) {
		return Promise.resolve();
	}
	if (sdkPromise) {
		return sdkPromise;
	}
	sdkPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = GIS_SDK_URL;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () =>
			reject(new Error("Failed to load the Google Identity Services SDK"));
		document.head.appendChild(script);
	});
	return sdkPromise;
};

// Preload the SDK on import so that, by the time the user clicks, requestCode()
// can open the popup without losing the gesture.
if (typeof window !== "undefined") {
	loadSdk().catch(() => {});
}

const ensureClient = async () => {
	await loadSdk();
	if (!codeClient) {
		codeClient = window.google.accounts.oauth2.initCodeClient({
			client_id: GOOGLE_WEB_CLIENT_ID,
			scope: "openid email profile",
			ux_mode: "popup",
			callback: response => {
				if (!pending) {
					return;
				}
				const { resolve, reject } = pending;
				pending = null;
				if (response && response.code) {
					resolve({ authorizationCode: response.code });
				} else {
					reject(response || new Error("Google sign-in failed"));
				}
			},
			error_callback: err => {
				if (!pending) {
					return;
				}
				const { reject } = pending;
				pending = null;
				reject(err);
			}
		});
	}
};

// Opens the Google popup and resolves with { authorizationCode } for the server.
export const signInWithGoogle = async () => {
	await ensureClient();
	return new Promise((resolve, reject) => {
		pending = { resolve, reject };
		codeClient.requestCode();
	});
};

// GIS reports a user-cancelled / blocked popup via these markers.
export const isGoogleCancel = error =>
	Boolean(
		error &&
			(error.type === "popup_closed" ||
				error.type === "popup_failed_to_open" ||
				error.error === "popup_closed_by_user" ||
				error.error === "access_denied")
	);
